import { useEffect, useRef, useState } from "react";
import { Pause, Play, Volume2, VolumeX } from "lucide-react";
import { CHAPTER_ONE_AUDIO_URL } from "@/content";

const BAR_COUNT = 56;
const BAR_HEIGHTS = Array.from({ length: BAR_COUNT }, (_, i) =>
  Math.round(10 + 14 * Math.abs(Math.sin(i * 0.55)) + 8 * Math.abs(Math.sin(i * 1.7 + 1)))
);

const formatTime = (s) => {
  if (!isFinite(s) || s <= 0) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

const ChapterOnePlayer = ({ src = CHAPTER_ONE_AUDIO_URL, label = "Chapter One · Excerpt" }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
    } else {
      await a.play().catch(() => {});
    }
  };

  const seekTo = (fraction) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    a.currentTime = fraction * duration;
    setCurrent(a.currentTime);
  };

  const onBarsClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    seekTo(Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)));
  };

  const onBarsKey = (e) => {
    if (!duration) return;
    if (e.key === "ArrowRight") {
      e.preventDefault();
      seekTo(Math.min(1, (current + 5) / duration));
    }
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      seekTo(Math.max(0, (current - 5) / duration));
    }
  };

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = muted ? 0 : volume;
  }, [volume, muted]);

  const progress = duration ? current / duration : 0;

  return (
    <div data-testid="chapter-one-player" className="w-full">
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
      />

      <div className="flex items-center gap-5 md:gap-8">
        <button
          onClick={toggle}
          data-testid="chapter-one-play-button"
          aria-label={playing ? "Pause Chapter One" : "Play Chapter One"}
          className="shrink-0 h-14 w-14 rounded-full bg-teal text-ivory flex items-center justify-center hover:bg-deepteal transition-colors duration-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-2 focus-visible:ring-offset-ivory"
        >
          {playing ? <Pause size={20} /> : <Play size={20} className="translate-x-[1px]" />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-4 mb-3">
            <span
              className="font-sans text-[11px] uppercase tracking-[0.3em] text-charcoal"
              data-testid="chapter-one-label"
            >
              {label}
            </span>
            <span className="text-xs text-taupe tabular-nums" data-testid="chapter-one-time">
              {formatTime(current)} / {formatTime(duration)}
            </span>
          </div>

          <div
            role="slider"
            tabIndex={0}
            aria-label="Chapter One listening progress"
            aria-valuemin={0}
            aria-valuemax={Math.round(duration)}
            aria-valuenow={Math.round(current)}
            onClick={onBarsClick}
            onKeyDown={onBarsKey}
            data-testid="chapter-one-progress"
            className="flex items-end gap-[2px] md:gap-[3px] h-10 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-teal"
          >
            {BAR_HEIGHTS.map((h, i) => (
              <span
                key={i}
                aria-hidden="true"
                className={`flex-1 transition-colors duration-500 ${
                  i / BAR_COUNT < progress ? "bg-teal" : "bg-stone"
                }`}
                style={{ height: `${(h / 32) * 100}%` }}
              />
            ))}
          </div>
        </div>

        <div className="hidden sm:flex items-center gap-3 shrink-0">
          <button
            onClick={() => setMuted(!muted)}
            data-testid="chapter-one-mute-button"
            aria-label={muted ? "Unmute Chapter One" : "Mute Chapter One"}
            className="text-taupe hover:text-charcoal transition-colors duration-500"
          >
            {muted || volume === 0 ? <VolumeX size={17} /> : <Volume2 size={17} />}
          </button>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={muted ? 0 : volume}
            onChange={(e) => {
              setVolume(Number(e.target.value));
              setMuted(false);
            }}
            aria-label="Chapter One volume"
            data-testid="chapter-one-volume"
            className="w-20 accent-teal cursor-pointer"
          />
        </div>
      </div>
    </div>
  );
};

export default ChapterOnePlayer;
